import Image from '../../../components/AppImage';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { TrustSignal } from '../types';

interface TrustSignalsSectionProps {
  trustSignals: TrustSignal[];
}

const TrustSignalsSection = ({ trustSignals }: TrustSignalsSectionProps) => {
  const { t, isLoading: translationsLoading } = useTranslation();
  
  const getTranslation = (key: string, fallback: string): string => {
    if (translationsLoading) return fallback;
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            {getTranslation('landing.trust.headline', 'Recognized & Trusted Nationwide')}
          </h2>
          
          <p className="text-lg text-muted-foreground">
            {getTranslation('landing.trust.description', 'Partnering with leading organizations to empower Tanzanian youth')}
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 elevation-md">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            {getTranslation('landing.trust.partnersTitle', 'Our Partners & Certifications')}
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
            {trustSignals.map((signal) => (
              <div
                key={signal.id}
                className="group relative aspect-square bg-muted/50 rounded-xl p-4 flex items-center justify-center hover:bg-primary/5 transition-smooth"
                title={`${signal.name} - ${signal.type.charAt(0).toUpperCase() + signal.type.slice(1)}`}
              >
                <Image
                  src={signal.logo}
                  alt={signal.alt || signal.name}
                  className="w-full h-full object-contain opacity-60 group-hover:opacity-100 transition-smooth"
                />
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary rounded-xl transition-smooth" />
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-foreground text-background text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-lg">
                  <div className="font-semibold">{signal.name}</div>
                  <div className="text-xs opacity-90 capitalize">{signal.type}</div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-foreground"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSignalsSection;